/**
 * Types for ad group creation flow
 */

export type BiddingStrategy =
  | 'INHERIT'
  | 'MANUAL_CPC'
  | 'TARGET_CPA'
  | 'TARGET_ROAS'
  | 'MAXIMIZE_CLICKS'
  | 'MAXIMIZE_CONVERSIONS'
  | 'MAXIMIZE_CONVERSION_VALUE';

export interface CreateAdGroupRequest {
  name: string;
  status: 'ENABLED' | 'PAUSED';
  bidding_strategy: BiddingStrategy;
  cpc_bid_micros?: number;
  target_cpa_micros?: number;
  target_roas?: number;
  campaign_type?: string;
  // Ad creation (required)
  ad: {
    // Common fields
    ad_name?: string;  // Ad name visible in Google Ads console
    headlines: string[];
    descriptions: string[];
    final_url: string;
    // RSA-specific fields
    path1?: string;
    path2?: string;
    // RDA-specific fields
    long_headline?: string;
    business_name?: string;
    landscape_image_ids?: string[];
    square_image_ids?: string[];
    logo_square_ids?: string[];
    logo_landscape_ids?: string[];
    // Demand Gen-specific fields
    call_to_action?: string;
    demand_gen_ad_type?: 'MULTI_ASSET' | 'VIDEO' | 'CAROUSEL';
    portrait_image_ids?: string[];
    logo_image_ids?: string[];
    video_ids?: string[];
    long_headlines?: string[];
    carousel_cards?: Array<{
      headline: string;
      image_id: string;
      final_url: string;
    }>;
  };
  // Audience targeting (optional, Demand Gen campaigns only)
  audience_targeting?: {
    // Audience resource names to add as targeting criteria
    audience_resource_names?: string[];
    // Demographic targeting (empty arrays = target all)
    demographics?: {
      age_ranges?: string[];
      genders?: string[];
      income_ranges?: string[];
      parental_statuses?: string[];
    };
  };
}

export interface CreateAdGroupResponse {
  success: boolean;
  ad_group_id: string;
  ad_group_name: string;
  campaign_id: string;
  message: string;
  task_id?: string;
}

export interface AdGroupFormData {
  // Step 1: Basic Info
  name: string;
  status: 'ENABLED' | 'PAUSED';

  // Step 2: Bidding Strategy
  bidding_strategy: BiddingStrategy;
  cpc_bid_dollars?: string; // Store as string for controlled input, convert to micros on submit
  target_cpa_dollars?: string;
  target_roas?: string;

  // Step 3: Ad Creation (Responsive Search Ad)
  headlines: string[]; // 3-15 headlines
  descriptions: string[]; // 2-4 descriptions
  finalUrl: string;
  path1?: string; // Optional display path
  path2?: string; // Optional display path
}

export const BIDDING_STRATEGIES: Array<{
  value: BiddingStrategy;
  label: string;
  description: string;
  requiresInput: boolean;
  inputType?: 'cpc' | 'cpa' | 'roas';
}> = [
  {
    value: 'INHERIT',
    label: 'Inherit from Campaign',
    description: 'Use the bidding strategy set at the campaign level (recommended)',
    requiresInput: false,
  },
  {
    value: 'MANUAL_CPC',
    label: 'Manual CPC',
    description: 'Set your own maximum cost-per-click bid for each ad group',
    requiresInput: true,
    inputType: 'cpc',
  },
  {
    value: 'TARGET_CPA',
    label: 'Target CPA',
    description: 'Set a target cost per acquisition and let Google optimize bids',
    requiresInput: true,
    inputType: 'cpa',
  },
  {
    value: 'TARGET_ROAS',
    label: 'Target ROAS',
    description: 'Set a target return on ad spend and let Google optimize bids',
    requiresInput: true,
    inputType: 'roas',
  },
  {
    value: 'MAXIMIZE_CLICKS',
    label: 'Maximize Clicks',
    description: 'Get as many clicks as possible within your budget',
    requiresInput: false,
  },
  {
    value: 'MAXIMIZE_CONVERSIONS',
    label: 'Maximize Conversions',
    description: 'Get the most conversions possible within your budget',
    requiresInput: false,
  },
  {
    value: 'MAXIMIZE_CONVERSION_VALUE',
    label: 'Maximize Conversion Value',
    description: 'Get the most conversion value possible within your budget',
    requiresInput: false,
  },
];

export const CAMPAIGN_TYPES_WITH_AD_GROUPS = [
  'SEARCH',
  'DISPLAY',
  'SHOPPING',
  'AI_MAX_SEARCH',
  'DEMAND_GEN',
] as const;

export type CampaignTypeWithAdGroups = typeof CAMPAIGN_TYPES_WITH_AD_GROUPS[number];

/**
 * Check if a campaign type supports ad groups
 */
export function supportsAdGroups(campaignType: string | undefined): boolean {
  if (!campaignType) return false;
  return CAMPAIGN_TYPES_WITH_AD_GROUPS.includes(campaignType as CampaignTypeWithAdGroups);
}

/**
 * Convert dollars to micros for API requests
 */
export function dollarsToMicros(dollars: string | number): number {
  const amount = typeof dollars === 'string' ? parseFloat(dollars) : dollars;
  return Math.round(amount * 1000000);
}

/**
 * Convert micros to dollars for display
 */
export function microsToDollars(micros: number): number {
  return micros / 1000000;
}

/**
 * Check if a bidding strategy allows manual CPC bid control
 * Only Manual CPC bidding strategy allows users to set their own max CPC bids
 * All other strategies are automated (Google controls the bids)
 */
export function allowsManualCpcControl(biddingStrategy: string | undefined): boolean {
  if (!biddingStrategy) return false;

  // Handle both "MANUAL_CPC" and "Manual CPC" formats
  const normalized = biddingStrategy.toUpperCase().replace(/\s+/g, '_');
  return normalized === 'MANUAL_CPC';
}
